import {
  Box,
  ErrorMessage,
  FieldLabel,
  Flex,
  Form,
  GimaTag,
  Modal,
  RadioGroupField,
  Space,
  SubmitButton,
  Table,
} from '@/components';
import {
  BulkUpdateExcludedFlagsFormSchema,
  ExclusionSettingsOperationLabelModifier,
  exclusionSettingsOperation,
} from '@/features/tenant-manager/models';
import { useForm } from '@/hooks';
import { filterRecursively, transformToDataWithChildren } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ComponentProps, type FC, type Key, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import type { z } from 'zod';
import { useOrganizationListRecursive } from '../../hooks';
import { IsExcludedText } from './IsExcludedText';

type Props = {
  open: boolean;
  onCancel: () => void;
} & Pick<ComponentProps<typeof BulkUpdateExcludedFlagsForm>, 'onSubmit'>;
export const BulkUpdateExcludedFlagsFormModal: FC<Props> = ({
  open,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      centered
      open={open}
      width={960}
      title={t('title-bulk-update-excluded-flags')}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Box paddingTop={8}>
        <BulkUpdateExcludedFlagsForm onSubmit={onSubmit} />
      </Box>
    </Modal>
  );
};

type FormValues = z.infer<typeof BulkUpdateExcludedFlagsFormSchema>;

const BulkUpdateExcludedFlagsForm: FC<{
  onSubmit: (values: FormValues) => void;
}> = ({ onSubmit }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const pageNumber = Number(searchParams.get('page') ?? 1);
  const referenceFiscalYear = searchParams.get('referenceFiscalYear');
  const showExcludedOptions = searchParams.get('showExcludedOptions') ?? false;
  const organizations = useOrganizationListRecursive({
    page: pageNumber > 0 ? pageNumber : 1,
    referenceFiscalYear: referenceFiscalYear
      ? Number(referenceFiscalYear)
      : undefined,
    showExcludedOptions: Boolean(showExcludedOptions),
  });
  const {
    watch,
    control,
    formState: { errors, isSubmitting },
    setValue,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      organizationIds: [],
    },
    resolver: zodResolver(BulkUpdateExcludedFlagsFormSchema),
  });
  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, Array<boolean | Key> | null>
  >({});
  const [organizationIds] = watch(['organizationIds']);
  const onSelectChange = (newSelectedRowKeys: Key[]) => {
    setValue('organizationIds', newSelectedRowKeys as string[]);
  };
  const rowSelection = {
    selectedRowKeys: organizationIds,
    onChange: onSelectChange,
  };
  if (!organizations.data) {
    return null;
  }
  const dataSource = transformToDataWithChildren(organizations.data.results);

  const filteredDataSource =
    'isExcludedFromOptions' in filteredInfo &&
    typeof filteredInfo.isExcludedFromOptions?.at(-1) === 'boolean'
      ? filterRecursively(dataSource, {
          key: 'isExcludedFromOptions',
          value: filteredInfo.isExcludedFromOptions.at(-1),
        })
      : dataSource;
  return (
    <Form
      layout="vertical"
      leavePageAlertCondition={null}
      onFinish={handleSubmit(onSubmit)}
    >
      <RadioGroupField
        name="operation"
        label={t('update-excluded-flags-operation')}
        control={control}
        errors={errors}
        options={exclusionSettingsOperation.options.map((value) => ({
          value,
          label: ExclusionSettingsOperationLabelModifier[value],
        }))}
      />
      <Form.Item
        label={
          <Space>
            <FieldLabel text={t('target-organization')} required />
            <ErrorMessage errors={errors} name="organizationIds" />
          </Space>
        }
      >
        <Table
          enableSticky={false}
          rowKey="id"
          rowSelection={rowSelection}
          onRow={({ id }) => ({
            onClick: () => {
              const newOrganizationIds = organizationIds.includes(id)
                ? organizationIds.filter((k) => k !== id)
                : [...organizationIds, id];
              onSelectChange(newOrganizationIds);
            },
          })}
          dataSource={filteredDataSource}
          scroll={{ y: '360px' }}
          columns={[
            {
              title: t('organization-code'),
              key: 'code',
              dataIndex: 'code',
              width: 200,
            },
            {
              title: t('name'),
              key: 'name',
              dataIndex: 'name',
              render: (value, record) => (
                <Flex gap={4}>
                  <Box>{value}</Box>
                  <GimaTag isIntegrated={record.isIntegrated} />
                </Flex>
              ),
            },
            {
              title: t('is-excluded-from-options'),
              key: 'isExcludedFromOptions',
              dataIndex: 'isExcludedFromOptions',
              render: (isExcluded) => (
                <IsExcludedText isExcluded={isExcluded} />
              ),
              width: 100,
              filters: [
                {
                  text: t('is-excluded'),
                  value: true,
                },
                {
                  text: t('is-not-excluded'),
                  value: false,
                },
              ],
              filtered: filteredInfo.isExcludedFromOptions
                ? filteredInfo.isExcludedFromOptions.length > 0
                : false,
              filteredValue: filteredInfo.isExcludedFromOptions,
              filterMultiple: false,
            },
          ]}
          onChange={(_pagination, filters) => {
            setFilteredInfo(filters);
          }}
          expandable={{ defaultExpandAllRows: false }}
          pagination={false}
        />

        <Flex style={{ marginTop: 16 }} justify="flex-end">
          <SubmitButton text={t('apply')} loading={isSubmitting} />
        </Flex>
      </Form.Item>
    </Form>
  );
};
